import { Shield, ShieldOff, AlertTriangle, Info } from 'lucide-react';

/**
 * GuardrailsToggle
 *
 * Single, prominent control that lets the user flip between:
 *   • Standard Report  — confidence guardrails ON (default; safe/operational).
 *   • Indicative Report — guardrails OFF (raw values shown, even at low conf).
 *
 * Visible across all analysis areas (Growth vs Model, Spreadsheet
 * Comparison, …) — both pages used to render two large nearly-identical
 * inline blocks; this component consolidates them and amps up the visual
 * weight so the user can never miss it.
 *
 * Props:
 *   active   bool — true when guardrails are OFF (override active).
 *   onChange (next: bool) => void — toggle handler.
 *   ariaLabel optional accessibility label.
 *   sticky   bool — when true, renders sticky at top of scroll container
 *            (use only on the *primary* placement; embedded copies should
 *            pass `sticky={false}`).
 *   testId   override the data-testid prefix (defaults to "guardrails-toggle").
 */
export default function GuardrailsToggle({
  active,
  onChange,
  ariaLabel = 'Toggle report guardrails',
  sticky = false,
  testId = 'guardrails-toggle',
}) {
  const handleToggle = () => onChange?.(!active);

  // Distinct colour stories for the two states so the user can always tell at
  // a glance which mode is in effect — green/safe when guardrails are ON,
  // amber/warning when OFF (we still let the user proceed, but visually it's
  // unmistakable that they're seeing unverified numbers).
  const containerCls = active
    ? 'border-amber-300 bg-gradient-to-r from-amber-50 via-orange-50 to-amber-50 ring-1 ring-amber-200'
    : 'border-emerald-300 bg-gradient-to-r from-emerald-50 via-white to-emerald-50';
  const iconCls = active ? 'text-amber-600' : 'text-emerald-700';
  const trackCls = active ? 'bg-amber-500' : 'bg-emerald-500';
  const knobTranslate = active ? 'translate-x-7' : 'translate-x-1';
  const StateIcon = active ? ShieldOff : Shield;
  const stateBadgeCls = active
    ? 'bg-amber-200/80 text-amber-900 border-amber-400'
    : 'bg-emerald-200/80 text-emerald-900 border-emerald-400';
  const stateBadgeText = active ? 'Guardrails OFF' : 'Guardrails ON';
  const titleText = active ? 'Indicative Report Mode' : 'Standard Report Mode';
  const subText = active
    ? 'Showing all available data — including low-confidence and unconfirmed signals. Treat as indication only.'
    : 'Showing only findings that meet the platform\'s confidence threshold. Toggle off to see all underlying signals.';

  return (
    <div
      className={[
        'rounded-xl border-2 shadow-sm transition-colors',
        containerCls,
        sticky ? 'sticky top-2 z-30 backdrop-blur-sm' : '',
      ].join(' ')}
      data-testid={`${testId}-card`}
      data-state={active ? 'off' : 'on'}
    >
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 p-3 sm:p-4">
        {/* Icon + state label block */}
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <div
            className={[
              'flex items-center justify-center rounded-full h-10 w-10 flex-shrink-0',
              active ? 'bg-amber-100' : 'bg-emerald-100',
            ].join(' ')}
          >
            <StateIcon className={`h-5 w-5 ${iconCls}`} />
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <p className="text-sm sm:text-base font-bold text-slate-900">{titleText}</p>
              <span
                className={`inline-flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full border ${stateBadgeCls}`}
                data-testid={`${testId}-state-badge`}
              >
                {active && <AlertTriangle className="h-3 w-3" />}
                {stateBadgeText}
              </span>
            </div>
            <p className="text-xs text-slate-600 leading-snug mt-0.5">{subText}</p>
          </div>
        </div>

        {/* Toggle switch */}
        <button
          type="button"
          role="switch"
          aria-checked={active}
          aria-label={ariaLabel}
          onClick={handleToggle}
          className={[
            'group relative h-7 w-14 rounded-full transition-colors flex-shrink-0 outline-none',
            'focus-visible:ring-2 focus-visible:ring-offset-2',
            active ? 'focus-visible:ring-amber-500' : 'focus-visible:ring-emerald-500',
            trackCls,
          ].join(' ')}
          data-testid={`${testId}-switch`}
        >
          <span
            className={[
              'absolute top-1 h-5 w-5 rounded-full bg-white shadow-md transition-transform',
              knobTranslate,
            ].join(' ')}
          />
        </button>
      </div>

      {/* Persistent unverified-data warning when OFF */}
      {active && (
        <div
          className="border-t border-amber-200 bg-amber-100/40 rounded-b-xl px-4 py-2 flex items-start gap-2"
          data-testid={`${testId}-warning`}
        >
          <AlertTriangle className="h-4 w-4 text-amber-700 mt-0.5 flex-shrink-0" />
          <p className="text-xs text-amber-900 leading-snug">
            <span className="font-semibold">Unverified data displayed.</span>{' '}
            Estimates shown in this mode bypass confidence gates and may be based on unconfirmed jumps,
            sparse samples, or low data quality. Do not use for operational decisions without independent
            validation.
          </p>
        </div>
      )}

      {/* Tiny inline help line when ON, so users always know the toggle exists */}
      {!active && (
        <div
          className="border-t border-emerald-200 bg-emerald-100/30 rounded-b-xl px-4 py-1.5 flex items-center gap-2"
          data-testid={`${testId}-hint`}
        >
          <Info className="h-3.5 w-3.5 text-emerald-700 flex-shrink-0" />
          <p className="text-[11px] text-emerald-900 leading-snug">
            Toggle off to view all underlying signals (including low-confidence ones).
          </p>
        </div>
      )}
    </div>
  );
}
